import React, { useState, useMemo } from 'react';
import {
  Film,
  ShoppingBag,
  Plane,
  Stethoscope,
  HeartHandshake,
  Home,
  Briefcase,
  Sun,
  UtensilsCrossed,
  Trophy,
  Activity,
  Laugh,
  Wine,
  Coffee,
  BookOpen,
  Sparkles,
  Utensils,
  Music,
  Landmark,
  PartyPopper,
  Check,
  X,
  Search,
  SlidersHorizontal,
  IndianRupee,
  Layers,
  CheckCircle2,
  AlertCircle,
  Plus,
  Minus,
  RotateCcw,
  Compass,
  Car
} from 'lucide-react';
import { CompanionServiceRate } from '../types';
import { ALL_COMPANION_SERVICES, getDefaultCompanionServices } from '../data/servicesData';

interface CompanionServicesManagerProps {
  services: CompanionServiceRate[];
  onChangeServices: (updated: CompanionServiceRate[]) => void;
  onShowToast: (msg: string, type?: 'success' | 'heart' | 'info') => void;
}

const CATEGORY_TABS = [
  'All Services (22)',
  'Care & Assistance',
  'Entertainment & Nightlife',
  'Dining & Social',
  'Sports & Games',
  'Lifestyle & Travel',
  'Corporate & Learning'
];

export const CompanionServicesManager: React.FC<CompanionServicesManagerProps> = ({
  services,
  onChangeServices,
  onShowToast
}) => {
  const [selectedCategoryTab, setSelectedCategoryTab] = useState<string>('All Services (22)');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [uniformRateInput, setUniformRateInput] = useState<string>('1500');
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);

  // Ensure all 22 services are present
  const fullServices = useMemo(() => {
    const existingMap = new Map<string, CompanionServiceRate>(
      (services || []).map((s) => [s.id, s])
    );
    return ALL_COMPANION_SERVICES.map((def) => {
      const existing = existingMap.get(def.id);
      if (existing) {
        return {
          id: def.id,
          name: def.name,
          category: def.category,
          description: def.description,
          isAvailable: existing.isAvailable,
          hourlyRate: existing.hourlyRate
        };
      }
      return {
        id: def.id,
        name: def.name,
        category: def.category,
        description: def.description,
        isAvailable: true,
        hourlyRate: def.defaultRate
      };
    });
  }, [services]);

  // Statistics
  const availableCount = fullServices.filter((s) => s.isAvailable).length;
  const rates = fullServices.filter((s) => s.isAvailable).map((s) => s.hourlyRate);
  const avgRate = rates.length > 0 ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : 0;
  const minRate = rates.length > 0 ? Math.min(...rates) : 0;
  const maxRate = rates.length > 0 ? Math.max(...rates) : 0;

  // Filtered Services list
  const filteredServices = useMemo(() => {
    return fullServices.filter((srv) => {
      // Category filter
      if (!selectedCategoryTab.startsWith('All Services')) {
        if (selectedCategoryTab === 'Care & Assistance' && srv.category !== 'Care & Assistance') return false;
        if (selectedCategoryTab === 'Entertainment & Nightlife' && !['Entertainment & Arts', 'Entertainment & Nightlife', 'Dining & Nightlife'].includes(srv.category)) return false;
        if (selectedCategoryTab === 'Dining & Social' && !['Dining & Social', 'Social & Casual', 'Social & Celebrations'].includes(srv.category)) return false;
        if (selectedCategoryTab === 'Sports & Games' && srv.category !== 'Sports & Games') return false;
        if (selectedCategoryTab === 'Lifestyle & Travel' && !['Lifestyle & Social', 'Adventure & Travel', 'Outdoors & Active', 'Culinary & Creative', 'Arts & Culture', 'Travel & Sightseeing', 'Travel & Transit'].includes(srv.category)) return false;
        if (selectedCategoryTab === 'Corporate & Learning' && !['Corporate & Business', 'Education & Learning'].includes(srv.category)) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = srv.name.toLowerCase().includes(q);
        const matchCat = srv.category.toLowerCase().includes(q);
        const matchDesc = srv.description?.toLowerCase().includes(q);
        if (!matchName && !matchCat && !matchDesc) return false;
      }

      return true;
    });
  }, [fullServices, selectedCategoryTab, searchQuery]);

  // Helper for rendering icons
  const renderServiceIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case 'movies':
        return <Film className="w-5 h-5 text-purple-600" />;
      case 'shopping':
        return <ShoppingBag className="w-5 h-5 text-pink-600" />;
      case 'travel':
        return <Plane className="w-5 h-5 text-sky-600" />;
      case 'medical help':
        return <Stethoscope className="w-5 h-5 text-emerald-600" />;
      case 'elder care':
        return <HeartHandshake className="w-5 h-5 text-rose-600" />;
      case 'domestic help':
        return <Home className="w-5 h-5 text-amber-600" />;
      case 'business events':
        return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'outdoor events':
        return <Sun className="w-5 h-5 text-orange-600" />;
      case 'baking/cooking':
        return <UtensilsCrossed className="w-5 h-5 text-amber-600" />;
      case 'sporting events':
        return <Trophy className="w-5 h-5 text-yellow-600" />;
      case 'playing sports':
        return <Activity className="w-5 h-5 text-emerald-600" />;
      case 'comedy club':
        return <Laugh className="w-5 h-5 text-indigo-600" />;
      case 'going to bar':
        return <Wine className="w-5 h-5 text-rose-600" />;
      case 'hanging out':
        return <Coffee className="w-5 h-5 text-amber-700" />;
      case 'tutoring':
        return <BookOpen className="w-5 h-5 text-blue-600" />;
      case 'clubbing':
        return <Sparkles className="w-5 h-5 text-fuchsia-600" />;
      case 'dinner':
        return <Utensils className="w-5 h-5 text-red-600" />;
      case 'music':
        return <Music className="w-5 h-5 text-purple-600" />;
      case 'culture':
        return <Landmark className="w-5 h-5 text-teal-600" />;
      case 'parties':
        return <PartyPopper className="w-5 h-5 text-pink-600" />;
      case 'tourist guide':
        return <Compass className="w-5 h-5 text-emerald-600" />;
      case 'driver':
        return <Car className="w-5 h-5 text-blue-600" />;
      default:
        return <Sparkles className="w-5 h-5 text-pink-600" />;
    }
  };

  // Toggle single service availability
  const toggleAvailability = (id: string) => {
    const updated = fullServices.map((srv) =>
      srv.id === id ? { ...srv, isAvailable: !srv.isAvailable } : srv
    );
    onChangeServices(updated);
    const target = updated.find((s) => s.id === id);
    onShowToast(
      `${target?.name} is now ${target?.isAvailable ? 'Available ✅' : 'Unavailable ⛔'}`,
      'info'
    );
  };

  // Adjust rate by delta (enforcing minimum ₹600/hr)
  const adjustRate = (id: string, delta: number) => {
    const updated = fullServices.map((srv) => {
      if (srv.id === id) {
        const next = Math.max(600, Math.min(25000, srv.hourlyRate + delta));
        return { ...srv, hourlyRate: next };
      }
      return srv;
    });
    onChangeServices(updated);
  };

  // Direct rate change input (enforcing minimum ₹600/hr)
  const setExactRate = (id: string, value: number) => {
    const safeVal = isNaN(value) ? 600 : Math.max(600, Math.min(50000, value));
    const updated = fullServices.map((srv) =>
      srv.id === id ? { ...srv, hourlyRate: safeVal } : srv
    );
    onChangeServices(updated);
  };

  // Quick preset rate buttons
  const setPresetRate = (id: string, rate: number) => {
    const updated = fullServices.map((srv) =>
      srv.id === id ? { ...srv, hourlyRate: rate } : srv
    );
    onChangeServices(updated);
  };

  // Bulk: Enable All
  const handleEnableAll = () => {
    const updated = fullServices.map((srv) => ({ ...srv, isAvailable: true }));
    onChangeServices(updated);
    onShowToast('All 22 services are now enabled and marked as available! ✨', 'success');
  };

  // Bulk: Disable All
  const handleDisableAll = () => {
    const updated = fullServices.map((srv) => ({ ...srv, isAvailable: false }));
    onChangeServices(updated);
    onShowToast('All services marked as unavailable.', 'info');
  };

  // Bulk: Set Uniform Rate across all
  const handleApplyUniformRate = () => {
    const rateNum = parseInt(uniformRateInput, 10);
    if (isNaN(rateNum) || rateNum < 200) {
      onShowToast('Please enter a valid hourly rate (min ₹200)', 'info');
      return;
    }
    const updated = fullServices.map((srv) => ({ ...srv, hourlyRate: rateNum }));
    onChangeServices(updated);
    setShowBulkModal(false);
    onShowToast(`Applied ₹${rateNum.toLocaleString('en-IN')}/hr across all 22 services! 💼`, 'success');
  };

  // Reset to Defaults
  const handleResetToDefaults = () => {
    const def = getDefaultCompanionServices();
    onChangeServices(def);
    onShowToast('Services & rates reset to standard defaults', 'info');
  };

  return (
    <div id="companion-services-manager" className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 space-y-5">
      {/* Header with Title & Context */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900">
                Choose a Service & Set Hourly Rates
              </h3>
              <p className="text-xs text-slate-500">
                Select which of the 22 services you are available for and set your hourly charges (₹/hr)
              </p>
            </div>
          </div>
        </div>

        {/* Quick summary status badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>{availableCount} of 20 Available</span>
          </span>
          {availableCount > 0 && (
            <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">
              Avg: ₹{avgRate.toLocaleString('en-IN')}/hr
            </span>
          )}
        </div>
      </div>

      {/* Overview Stat Strip & Bulk Action Toolbar */}
      <div className="bg-gradient-to-r from-pink-50/70 via-purple-50/50 to-indigo-50/40 rounded-2xl p-3.5 border border-pink-100/80 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-700">
          <span className="font-bold flex items-center gap-1 text-slate-900">
            <SlidersHorizontal className="w-3.5 h-3.5 text-pink-600" /> Quick Management:
          </span>
          <button
            type="button"
            onClick={handleEnableAll}
            className="px-2.5 py-1 bg-white hover:bg-emerald-50 text-emerald-700 font-bold rounded-lg border border-emerald-200 shadow-2xs transition-colors cursor-pointer text-[11px]"
          >
            Enable All 20
          </button>
          <button
            type="button"
            onClick={handleDisableAll}
            className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg border border-slate-200 shadow-2xs transition-colors cursor-pointer text-[11px]"
          >
            Disable All
          </button>
          <button
            type="button"
            onClick={() => setShowBulkModal(!showBulkModal)}
            className="px-2.5 py-1 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-lg shadow-2xs transition-colors cursor-pointer text-[11px] flex items-center gap-1"
          >
            <IndianRupee className="w-3 h-3" />
            <span>Set Uniform Rate for All</span>
          </button>
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="px-2 py-1 text-slate-400 hover:text-slate-600 text-[11px] transition-colors cursor-pointer flex items-center gap-1"
            title="Reset to defaults"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Defaults</span>
          </button>
        </div>

        {/* Min / Max rate hint */}
        {availableCount > 0 && (
          <div className="text-[11px] text-slate-500 font-medium shrink-0">
            Rate Range: <strong className="text-slate-800">₹{minRate.toLocaleString('en-IN')}</strong> - <strong className="text-slate-800">₹{maxRate.toLocaleString('en-IN')}/hr</strong>
          </div>
        )}
      </div>

      {/* Bulk Set Rate Mini Dropdown / Bar */}
      {showBulkModal && (
        <div className="p-3.5 bg-white border border-pink-200 rounded-2xl shadow-sm flex flex-wrap items-center gap-3 animate-in fade-in duration-150">
          <span className="text-xs font-bold text-slate-800">Set Uniform Hourly Rate across all services:</span>
          <div className="relative flex items-center">
            <span className="absolute left-2.5 text-xs text-slate-400 font-bold">₹</span>
            <input
              type="number"
              min={200}
              step={100}
              value={uniformRateInput}
              onChange={(e) => setUniformRateInput(e.target.value)}
              className="pl-6 pr-3 py-1 text-xs w-28 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:outline-none font-bold"
            />
          </div>
          <button
            type="button"
            onClick={handleApplyUniformRate}
            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-2xs cursor-pointer"
          >
            Apply to All
          </button>
          <button
            type="button"
            onClick={() => setShowBulkModal(false)}
            className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Category Tabs & Search Bar */}
      <div className="space-y-2.5 pt-1">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
          {/* Category Filter Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setSelectedCategoryTab(tab)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategoryTab === tab
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Search input */}
          <div className="relative shrink-0 sm:w-56">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search service..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* The 22 Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {filteredServices.map((service) => {
          const isOffered = service.isAvailable;
          return (
            <div
              key={service.id}
              className={`rounded-2xl border transition-all p-4 flex flex-col justify-between gap-3 ${
                isOffered
                  ? 'bg-white border-slate-200/90 shadow-2xs hover:border-pink-300 hover:shadow-xs'
                  : 'bg-slate-50/70 border-slate-200/50 opacity-75'
              }`}
            >
              {/* Card Header: Icon, Name, Category & Availability Toggle */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      isOffered ? 'bg-slate-100' : 'bg-slate-200/60'
                    }`}
                  >
                    {renderServiceIcon(service.name)}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-sm font-bold text-slate-900 leading-tight">
                        {service.name}
                      </h4>
                    </div>
                    <span className="inline-block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">
                      {service.category}
                    </span>
                    <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5 leading-snug">
                      {service.description}
                    </p>
                  </div>
                </div>

                {/* Availability Toggle Switch */}
                <button
                  type="button"
                  onClick={() => toggleAvailability(service.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-bold shrink-0 transition-all flex items-center gap-1 cursor-pointer ${
                    isOffered
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200'
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                  title={isOffered ? 'Click to mark as unavailable' : 'Click to offer this service'}
                >
                  {isOffered ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-600 stroke-[3]" />
                      <span>Available</span>
                    </>
                  ) : (
                    <>
                      <X className="w-3 h-3 text-slate-500" />
                      <span>Off</span>
                    </>
                  )}
                </button>
              </div>

              {/* Card Bottom: Rate Stepper & Input */}
              <div
                className={`pt-2.5 border-t flex flex-wrap items-center justify-between gap-2 ${
                  isOffered ? 'border-slate-100' : 'border-slate-200/50'
                }`}
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-slate-700">Hourly Rate:</span>
                  <span className="text-[10px] text-slate-400">(per hour)</span>
                </div>

                {/* Rate Stepper Controls */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={!isOffered}
                    onClick={() => adjustRate(service.id, -100)}
                    className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-30 text-slate-700 font-bold text-xs flex items-center justify-center transition-colors cursor-pointer"
                    title="-₹100"
                  >
                    <Minus className="w-3 h-3" />
                  </button>

                  <div className="relative flex items-center">
                    <span className="absolute left-2 text-xs font-bold text-slate-400">₹</span>
                    <input
                      type="number"
                      disabled={!isOffered}
                      min={100}
                      step={100}
                      value={service.hourlyRate}
                      onChange={(e) => setExactRate(service.id, parseInt(e.target.value, 10))}
                      className="w-24 pl-5 pr-2 py-1 text-xs font-extrabold text-slate-900 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 text-center"
                    />
                  </div>

                  <button
                    type="button"
                    disabled={!isOffered}
                    onClick={() => adjustRate(service.id, 100)}
                    className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-30 text-slate-700 font-bold text-xs flex items-center justify-center transition-colors cursor-pointer"
                    title="+₹100"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Quick Preset Rate Chips */}
              {isOffered && (
                <div className="flex items-center gap-1.5 overflow-x-auto pt-0.5">
                  <span className="text-[10px] text-slate-400 font-semibold shrink-0">Presets:</span>
                  {[800, 1000, 1200, 1500, 2000].map((pr) => (
                    <button
                      key={pr}
                      type="button"
                      onClick={() => setPresetRate(service.id, pr)}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold transition-colors cursor-pointer shrink-0 ${
                        service.hourlyRate === pr
                          ? 'bg-pink-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      ₹{pr}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <AlertCircle className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
          <p className="text-xs font-bold text-slate-700">No matching services found</p>
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setSelectedCategoryTab('All Services (20)');
            }}
            className="text-xs text-pink-600 hover:underline mt-1 font-semibold"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Bottom Save & Confirmation banner */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>
            Changes to your offered services & hourly rates are saved automatically in your profile.
          </span>
        </div>
        <button
          type="button"
          onClick={() => onShowToast('All service rates and availability confirmed! 💼✨', 'success')}
          className="px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer shrink-0 flex items-center gap-1.5"
        >
          <Check className="w-3.5 h-3.5" />
          <span>Confirm Services & Rates</span>
        </button>
      </div>
    </div>
  );
};
